/**
 * 2.0.0: 主题切换 — 简洁图标按钮，点击展开下拉选择
 *
 * 保持原有小方框+图标样式，点击后下拉显示：亮色 / 暗色 / 跟随系统。
 * 悬浮时橙色高亮。
 */

import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';
import type { ThemeMode } from '../stores/themeStore';

const OPTIONS: { mode: ThemeMode; icon: typeof Sun; labelKey: string }[] = [
  { mode: 'light', icon: Sun, labelKey: 'themeLight' },
  { mode: 'dark', icon: Moon, labelKey: 'themeDark' },
  { mode: 'system', icon: Monitor, labelKey: 'themeSystem' },
];

const getThemeIcon = (mode: ThemeMode) => {
  switch (mode) {
    case 'light': return Moon;
    case 'dark': return Sun;
    case 'system': return Monitor;
  }
};

export const ThemeToggle: React.FC = () => {
  const { t } = useTranslation();
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onOutside);
      return () => document.removeEventListener('mousedown', onOutside);
    }
  }, [open]);

  const Icon = getThemeIcon(mode);
  const tooltip = t('themeToggleToLight', '主题');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={tooltip}
        aria-label={tooltip}
        aria-expanded={open}
        className="
          relative w-8 h-8 flex items-center justify-center
          rounded-md border border-gray-300 dark:border-gray-600
          text-gray-500 dark:text-gray-400
          hover:text-orange-600 dark:hover:text-orange-400
          hover:border-orange-400 dark:hover:border-orange-500
          transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 dark:focus:ring-offset-gray-950
        "
      >
        <Icon key={mode} size={16} className="animate-[spin_300ms_ease-in-out_1]" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 py-1 min-w-[120px] rounded-lg
                     bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700
                     shadow-lg dark:shadow-xl z-50"
        >
          {OPTIONS.map(({ mode: m, icon: OptionIcon, labelKey }) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                setOpen(false);
              }}
              className={`
                w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                transition-colors duration-200
                hover:text-orange-600 dark:hover:text-orange-400
                hover:bg-orange-50 dark:hover:bg-orange-900/10
                ${m === mode ? 'text-orange-600 dark:text-orange-400 bg-orange-50/50 dark:bg-orange-900/20' : 'text-gray-700 dark:text-gray-300'}
              `}
            >
              <OptionIcon className="w-4 h-4 flex-shrink-0" />
              <span>{t(labelKey, m === 'light' ? '亮色' : m === 'dark' ? '暗色' : '跟随系统')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
